import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'

import { getNightCount } from '@/components/TripActivity/recentSelectionMapper'
import type { AbandonedHotelSelection, HotelDealMatch } from '@/components/TripActivity/types'
import { DAY_DATE_YEAR_FORMAT } from '@/components/TripActivity/utils'
import analytics from '@/shared-utils/analytics'
import fetchHotelDealMatches from '@/shared-utils/fetchHotelDealMatches'

function toYyyyMMdd(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined
  return format(parse(dateStr, DAY_DATE_YEAR_FORMAT, new Date()), 'yyyyMMdd')
}

export default function useSimilarHotels({
  showSimilarHotels,
  abandonedHotelSelection,
  appCode,
  appName,
  appVersion,
}: {
  showSimilarHotels: boolean
  abandonedHotelSelection: AbandonedHotelSelection | null
  appCode: string
  appName: string
  appVersion: string
}): { similarHotels: HotelDealMatch[]; isLoading: boolean } {
  const hotelId =
    abandonedHotelSelection?.hotelDealType === 'RtlHotel' ? abandonedHotelSelection.id : undefined
  const checkIn = toYyyyMMdd(abandonedHotelSelection?.checkInDate)
  const checkOut = toYyyyMMdd(abandonedHotelSelection?.checkOutDate)
  const rooms = abandonedHotelSelection?.numRooms
  const search = abandonedHotelSelection?.cityId ?? ''

  const {
    data: similarHotels = [],
    isLoading,
    error,
  } = useQuery<HotelDealMatch[]>({
    queryKey: [hotelId, checkIn, checkOut, rooms, search, appCode, appName, appVersion, 'similarHotels'],
    queryFn: async () => {
      const rawMatches = await fetchHotelDealMatches({
        hotelId: hotelId!,
        checkIn: checkIn!,
        checkOut: checkOut!,
        rooms: rooms!,
        search,
        appCode,
        appName,
        appVersion,
      })
      if (!rawMatches) return []
      return rawMatches
        .filter((m): m is typeof m & { reference: NonNullable<typeof m.reference> } => m.reference !== null)
        .map((m) => ({
          hotelId: m.hotelId,
          pclnId: m.pclnId,
          rank: m.rank,
          type: m.type,
          reference: m.reference,
          linkUrl: `/relax/at/${m.hotelId}/from/${checkIn}/to/${checkOut}/rooms/${rooms}`,
          numRooms: rooms ?? 1,
          numNights: abandonedHotelSelection
            ? getNightCount({
                checkInDate: abandonedHotelSelection.checkInDate,
                checkOutDate: abandonedHotelSelection.checkOutDate,
              })
            : 2,
        }))
    },
    staleTime: Infinity,
    retry: false,
    enabled: showSimilarHotels && !!hotelId && !!checkIn && !!checkOut && !!rooms && !!search,
  })

  if (error) {
    analytics.logError({
      message: 'Error thrown from useSimilarHotels',
      error: JSON.stringify(error),
      service: 'fetchHotelDealMatches',
    })
  }

  return { similarHotels, isLoading }
}
